import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../api/client';

const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

/** Header bell showing new case-progress notifications with an unread badge. */
export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const role = user?.role;
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.listNotifications(role);
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* offline / not signed – stay quiet */ }
  }, [role]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // poll for new case progress
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  const openMenu = async () => {
    setOpen((o) => !o);
    if (!open) await load();
  };

  const onItem = async (n) => {
    try { await api.markNotificationRead(n.id, role); } catch { /* ignore */ }
    setOpen(false);
    await load();
    navigate('/dosyalar');
  };

  const markAll = async () => {
    try { await api.markAllNotificationsRead(role); } catch { /* ignore */ }
    await load();
  };

  return (
    <div className="notif-bell" ref={ref}>
      <button type="button" className="notif-bell-btn" onClick={openMenu} aria-label={t('uyap.notifications')} title={t('uyap.notifications')}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-menu">
          <div className="notif-menu-head">
            <strong>{t('uyap.notifications')}</strong>
            {unread > 0 && <button type="button" className="notif-mark-all" onClick={markAll}>{t('uyap.markAllRead')}</button>}
          </div>
          {items.length === 0 ? (
            <p className="notif-empty">{t('uyap.noNotifications')}</p>
          ) : (
            <ul className="notif-list">
              {items.slice(0, 30).map((n) => (
                <li key={n.id}>
                  <button type="button" className={`notif-item ${n.read ? '' : 'unread'}${n.kind === 'hearing_brief' ? ' brief' : ''}`} onClick={() => onItem(n)}>
                    <span className="notif-item-title">{n.title}</span>
                    <span className="notif-item-meta">{n.caseTitle}{n.caseNo ? ` · ${n.caseNo}` : ''} · {fmt(n.ts)}</span>
                    {n.detail && <span className="notif-item-detail">{n.detail}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
